import {Component, inject, OnInit} from '@angular/core';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {LabelComponent} from '../../../../../../../shared/components/form/label/label.component';
import {AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import {CreationButtons} from '../../../../../../../shared/components/ui/creation-buttons/creation-buttons';
import {ErrorService} from '../../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {ClientesService} from '../../clientes.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CombosService} from '../../../../../../../shared/services/combos.service';
import {GenericCombo} from '../../../../../../../shared/services/interfaces/combos.interface';
import {Contrato, ContratoDocumento} from '../../clientes.interface';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {CheckboxComponent} from '@shared/components/form/checkbox/checkbox.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { Modal } from '@shared/components/common/modal/modal';
import {FileInputComponent} from '@shared/components/form/file-input/file-input.component';
import {MatIcon} from '@angular/material/icon';

interface DialogData {
  clienteId: number;
  contrato: Contrato | null;
}

@Component({
  selector: 'app-crear-contrato',
  imports: [
    HasPermissionDirective,
    LabelComponent,
    FormsModule,
    ReactiveFormsModule,
    CreationButtons,
    SelectFieldComponent,
    TextareaFieldComponent,
    CheckboxComponent,
    InputFieldComponent,
    AppFormComponent,
    Modal,
    FileInputComponent,
    MatIcon,
  ],
  templateUrl: './crear-contrato.html',
})
export class CrearContrato implements OnInit {

  saveForm!: FormGroup;
  loading = false;
  establecimientos: GenericCombo[] = [];
  isEditing = false;
  documentoActual: ContratoDocumento | null = null;
  documentoParaEliminar = false;

  get establecimientosOptions(): SelectOption[] {
    return this.establecimientos.map(e => ({ value: e.id, label: e.nombre }));
  }

  private fB = inject(FormBuilder);
  private clientesService = inject(ClientesService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CrearContrato>);
  private dialogData: DialogData = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.isEditing = !!this.dialogData.contrato;
    this.documentoActual = this.dialogData.contrato?.documento ?? null;
    this.getEstablecimientos();
    this.makeForm();
  }

  private getEstablecimientos(): void {
    this.combosService.getEstablecimientosCombo(this.dialogData.clienteId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.establecimientos = response.data;
          // Con un solo establecimiento el alta lo trae elegido
          const control = this.saveForm.get('cli_establecimiento_id');
          if (!this.isEditing && this.establecimientos.length === 1 && !control?.value) {
            control?.setValue(this.establecimientos[0].id);
          }
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    const contrato = this.dialogData.contrato;

    this.saveForm = this.fB.group({
      cli_cliente_id: [this.dialogData.clienteId, Validators.required],
      cli_establecimiento_id: [contrato?.cli_establecimiento_id || ''],
      nro_contrato: [contrato?.nro_contrato || ''],
      activo: [contrato?.activo ?? true],
      fecha_inicio: [contrato?.fecha_inicio || ''],
      fecha_fin: [contrato?.fecha_fin || ''],
      descripcion: [contrato?.descripcion || ''],
      documento: [null as File | null],
    }, {validators: this.fechasValidas()});
  }

  save(): void {
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      return;
    }

    if (this.isEditing) {
      this.update();
    } else {
      this.create();
    }
  }

  private create(): void {
    this.loading = true;
    const payload = this.preparePayload();

    this.clientesService.storeContrato(payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Contrato creado correctamente');
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  private update(): void {
    this.loading = true;
    const payload = this.preparePayload();

    this.clientesService.updateContrato(this.dialogData.contrato!.id, payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Contrato actualizado correctamente');
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onFileChange(file: File | null): void {
    if (file) {
      this.documentoParaEliminar = false;
    }
  }

  removeExistingDocument(): void {
    this.documentoActual = null;
    this.documentoParaEliminar = true;
    this.saveForm.patchValue({documento: null});
  }

  private preparePayload(): FormData {
    const formValue = this.saveForm.value;
    const payload = new FormData();

    payload.append('cli_cliente_id', String(formValue.cli_cliente_id));
    payload.append('cli_establecimiento_id', formValue.cli_establecimiento_id || '');
    payload.append('nro_contrato', formValue.nro_contrato || '');
    payload.append('activo', formValue.activo ? '1' : '0');
    payload.append('fecha_inicio', formValue.fecha_inicio || '');
    payload.append('fecha_fin', formValue.fecha_fin || '');
    payload.append('descripcion', formValue.descripcion || '');

    if (formValue.documento) {
      payload.append('documento', formValue.documento, formValue.documento.name);
    }

    if (this.documentoParaEliminar) {
      payload.append('eliminar_documento', '1');
    }

    if (this.isEditing) {
      payload.append('_method', 'PUT');
    }

    return payload;
  }

  private fechasValidas(): ValidatorFn {
    return (control: AbstractControl) => {
      const fechaInicio = control.get('fecha_inicio')?.value as string | null;
      const fechaFin = control.get('fecha_fin')?.value as string | null;

      return fechaInicio && fechaFin && fechaFin < fechaInicio ? {dateOrder: true} : null;
    };
  }

  cancel(): void {
    this.saveForm.reset();
    this.dialogRef.close();
  }
}
